import React, { useMemo, useCallback } from "react";

import { compact } from "lodash";
import { useHistory } from "react-router";

import {
  Container,
  Flex,
  Icon,
  Text,
  Stack,
  StackDivider,
  Menu,
  MenuButton,
  Portal,
  MenuList,
  MenuItem,
  IconButton,
} from "@chakra-ui/react";
import { DotsVerticalIcon } from "@heroicons/react/outline";
import { UserAvatarWithRole } from "../../utils/chakra/AvatarWithTooltip";

import useTranslations from "../../../hooks/useTranslations";
import useNotification from "../../../hooks/useNotification";
import useStore from "../../../store";

import {
  ReportFragment,
  useCreateReport,
  useDeleteReport,
} from "../../../graphql/generated/definitions";

import ReportLayout from "../layout/ReportLayout";
import SectionNavigationHeader from "../components/sections/SectionNavigationHeader";
import SectionReview from "../components/sections/SectionReview";
import ReportSignature from "../components/ReportSignature";
import ReportDownloadAsPDF from "../components/ReportDownloadAsPDF";
import ReportFormHeader from "../components/ReportFormHeader";
import ReportSignatureStatus from "../components/ReportSignatureStatus";

export default function ViewPage({ report }: { report: ReportFragment }) {
  const t = useTranslations();
  const history = useHistory();
  const { notifySuccess, notifyError } = useNotification();

  const sections = useStore((state) => state.report.sections);

  const reportFilledBy = useMemo(
    () =>
      compact([
        report.filledAt && {
          ...report.review.collaborator,
          role: "collaborator",
        },
        ...report.reportReferents
          .filter(({ filledAt }) => !!filledAt)
          .map(({ referent }) => ({ ...referent, role: "referent" })),
      ]),
    [report]
  );

  const [deleteReport] = useDeleteReport();
  const [createReport] = useCreateReport();

  const handleResetReport = useCallback(async () => {
    try {
      const input = { reviewId: report.review.id, type: report.type };
      await deleteReport({ variables: { input: { id: report.id } } });
      await createReport({ variables: { input } });
      notifySuccess({
        title: t(`reports.notification.reset.success`),
      });
    } catch {
      notifyError({
        title: t(`reports.notification.reset.error`),
      });
    }
    history.push(`/reviews/${report.review.id}`);
  }, [report]);

  const handleDeleteReport = useCallback(async () => {
    try {
      await deleteReport({ variables: { input: { id: report.id } } });
      notifySuccess({
        title: t(`reports.notification.delete.success`),
      });
    } catch {
      notifyError({
        title: t(`reports.notification.delete.error`),
      });
    }
    history.push(`/reviews/${report.review.id}`);
  }, [report]);

  return (
    <ReportLayout subHeader={<SectionNavigationHeader sections={sections} />}>
      <Flex
        direction="column"
        flexGrow={1}
        bgColor="primary.50"
        py={["0", "10"]}
        overflowY="auto"
      >
        <Container
          shadow="base"
          rounded={["none", "md"]}
          px="0"
          mb={["12", "0"]}
          maxW="container.xl"
          bgColor="white"
        >
          <ReportFormHeader report={report} />
          <Flex
            direction="column"
            roundedBottom="md"
            py={["10", "16"]}
            px={["6", "16"]}
          >
            <Text
              fontSize={["xl", "2xl"]}
              fontWeight="bold"
              color="brand"
            >
              {t("reports.view.completedBy")}
            </Text>
            <Stack direction="row" spacing="4" pb="2">
              {reportFilledBy.map(({ name, avatar, id, role }) => (
                <UserAvatarWithRole
                  rootProps={{ avatarProps: { size: "sm" } }}
                  key={id}
                  src={avatar}
                  name={name}
                  role={
                    t(
                      `users.roles.${role}` as `users.roles.${
                        | "collaborator"
                        | "referent"}`
                    ) as string
                  }
                />
              ))}
            </Stack>
            <Flex pt="3">
              <Stack spacing="4" direction={["column", "row"]}>
                <ReportDownloadAsPDF report={report} />
                {report.canSign && report.docusignEnvelopeStatus === null && (
                  <ReportSignature report={report} />
                )}
                {report.docusignEnvelopeStatus !== null && (
                  <ReportSignatureStatus
                    status={report.docusignEnvelopeStatus}
                    document={report.document}
                  />
                )}
              </Stack>
              {(report.canReset || report.canDestroy) && (
                <Flex flexGrow={1} justifyContent="flex-end">
                  <Menu id="report-actions">
                    <MenuButton
                      as={IconButton}
                      size="sm"
                      variant="outline"
                      icon={<Icon as={DotsVerticalIcon} />}
                    />
                    <Portal>
                      <MenuList>
                        {report.canDestroy && (
                          <MenuItem
                            name="report-actions-delete"
                            onClick={handleDeleteReport}
                          >
                            {t("reports.delete")}
                          </MenuItem>
                        )}
                        {report.canReset && (
                          <MenuItem
                            name="report-actions-reset"
                            onClick={handleResetReport}
                          >
                            {t("reports.reset")}
                          </MenuItem>
                        )}
                      </MenuList>
                    </Portal>
                  </Menu>
                </Flex>
              )}
            </Flex>
            <Stack
              direction="column"
              spacing="16"
              pt="16"
              divider={<StackDivider borderColor="gray.100" />}
            >
              {sections?.map((section, index) => (
                <SectionReview
                  key={section.id}
                  report={report}
                  sectionIndex={index}
                  path={`sections.${index}` as const}
                  section={section}
                />
              ))}
            </Stack>
          </Flex>
        </Container>
      </Flex>
    </ReportLayout>
  );
}
